import { Component } from 'react'
import * as Sentry from '@sentry/react'

// Key for tracking reload attempts to prevent infinite loops
const CHUNK_RELOAD_KEY = 'chunk_reload_attempt'
const CHUNK_RELOAD_TIMEOUT = 10000 // 10 seconds window

/**
 * Check if an error is a chunk loading failure (stale deployment)
 */
function isChunkLoadError(error) {
  const message = error?.message || error?.toString() || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError') ||
    // Network errors on .js files
    (message.includes('Failed to fetch') && message.includes('.js'))
  )
}

/**
 * Safely reload the page if we haven't recently tried
 * Prevents infinite reload loops
 */
function safeReload() {
  const lastAttempt = sessionStorage.getItem(CHUNK_RELOAD_KEY)
  const now = Date.now()

  if (lastAttempt && now - parseInt(lastAttempt, 10) < CHUNK_RELOAD_TIMEOUT) {
    console.warn('Chunk reload attempted recently, not auto-reloading to prevent loop')
    return false
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString())
  console.log('Auto-reloading due to stale chunk...')
  window.location.reload()
  return true
}

/**
 * Names that are almost certainly NOT the user's component — they're framework
 * / instrumentation wrappers. Skipping them lets the "failing component" field
 * surface the actual user component instead of the generic wrapper.
 *
 * Notable: in production minified bundles, Sentry's internal helper
 * `_sentry_idleSpan` accessor gets minified to a short name that happens to
 * collide with React's componentStack extraction. Filter known wrappers here.
 */
const WRAPPER_COMPONENT_NAMES = new Set([
  'SentryProfiler', 'Profiler', 'ErrorBoundary', 'Suspense', 'Fragment',
  'StrictMode', 'Route', 'Routes', 'Router', 'BrowserRouter',
  'QueryClientProvider', 'PersistQueryClientProvider',
  'KernelProvider',
])

/**
 * Extract component name from React error info.
 * Returns both the full chain and the first NON-wrapper component (best guess
 * at what actually threw).
 */
function extractComponentInfo(errorInfo) {
  if (!errorInfo?.componentStack) return null

  const stack = errorInfo.componentStack
  const lines = stack.split('\n').filter(line => line.trim())
  const components = lines.map(line => {
    const match = line.match(/at\s+(\w+)/)
    return match ? match[1] : null
  }).filter(Boolean)

  // Filter out 2-char names (usually minified framework internals like Sentry's
  // Ca/Pa/etc.) AND known wrapper component names. What's left is most likely
  // the user's actual component tree.
  const userComponents = components.filter(n =>
    n.length > 2 && !WRAPPER_COMPONENT_NAMES.has(n)
  )

  return {
    componentChain: components.slice(0, 8),      // raw first 8 for full context
    userChain:      userComponents.slice(0, 5),  // user components only
    failingComponent: userComponents[0] || components[0],
    fullStack: stack
  }
}

/**
 * Error Boundary component to gracefully handle React component errors
 * Prevents the entire app from crashing when a component throws an error
 * Automatically reloads on chunk loading failures (stale deployments)
 * Reports errors to Sentry with full context
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
      eventId: null, // Sentry event ID for user feedback
      componentInfo: null
    }
  }

  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error)
    return { hasError: true, error, isChunkError }
  }

  componentDidCatch(error, errorInfo) {
    // Extract component information for better debugging
    const componentInfo = extractComponentInfo(errorInfo)

    // Log error to console with useful context
    console.error('ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      component: componentInfo?.failingComponent,
      componentChain: componentInfo?.componentChain,
      route: window.location.pathname,
      url: window.location.href
    })

    this.setState({ errorInfo, componentInfo })

    // Auto-reload on chunk loading errors (stale deployment)
    if (isChunkLoadError(error)) {
      console.log('Detected chunk loading error - likely stale deployment')
      safeReload()
      return
    }

    // Report to Sentry with enhanced context
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo?.componentStack,
        failingComponent: componentInfo?.failingComponent,
        componentChain: componentInfo?.componentChain,
      },
      tags: {
        errorBoundary: true,
        component: componentInfo?.failingComponent || 'unknown',
        route: window.location.pathname,
      }
    })

    this.setState({ eventId })
    console.log(`Error reported to Sentry with ID: ${eventId}`)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      const { isChunkError } = this.state

      // For chunk errors, show a simpler "updating" message
      if (isChunkError) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-surface rounded-[6px] shadow-lg p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-accent animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-[#1a1a1a] mb-2">
                App Updated
              </h1>
              <p className="text-[#4a4a4a] mb-6">
                A new version is available. Refreshing automatically...
              </p>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-[6px] transition-colors"
              >
                Refresh Now
              </button>
            </div>
          </div>
        )
      }

      // Render fallback UI for other errors
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-surface rounded-[6px] shadow-lg p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger/10 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-danger"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h1 className="text-xl font-semibold text-[#1a1a1a] mb-2">
              Something went wrong
            </h1>

            <p className="text-[#4a4a4a] mb-4">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>

            {/* Always show component info and error ID for debugging */}
            <div className="mb-6 p-4 bg-background rounded-[6px] border border-[#ececec] text-left">
              {/* Show failing component (user-level, framework wrappers filtered out) */}
              {this.state.componentInfo?.failingComponent && (
                <p className="text-sm text-[#8a8a8a] mb-2">
                  <span className="font-medium text-[#4a4a4a]">Component:</span>{' '}
                  <code className="px-1 py-0.5 bg-danger/10 text-danger rounded">
                    {this.state.componentInfo.failingComponent}
                  </code>
                </p>
              )}
              {/* Show the user-level component chain so we can trace upward when
                  the "failing component" is ambiguous (e.g. minified names).
                  Always visible in prod — no dev gating. */}
              {this.state.componentInfo?.userChain?.length > 1 && (
                <p className="text-xs text-[#8a8a8a] mb-2">
                  <span className="font-medium text-[#4a4a4a]">Chain:</span>{' '}
                  <code className="px-1 py-0.5 bg-[#f5f5f5] rounded text-[11px]">
                    {this.state.componentInfo.userChain.join(' → ')}
                  </code>
                </p>
              )}
              <p className="text-sm text-[#8a8a8a] mb-2">
                <span className="font-medium text-[#4a4a4a]">Page:</span>{' '}
                <code className="px-1 py-0.5 bg-[#f5f5f5] rounded">{window.location.pathname}</code>
              </p>

              {/* Show Sentry event ID for support */}
              {this.state.eventId && (
                <p className="text-sm text-[#8a8a8a] mb-2">
                  <span className="font-medium text-[#4a4a4a]">Error ID:</span>{' '}
                  <code className="px-1 py-0.5 bg-[#f5f5f5] rounded text-xs">{this.state.eventId}</code>
                </p>
              )}

              {/* Show full error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mt-3 pt-3 border-t border-[#ececec]">
                  <p className="text-sm font-mono text-danger break-words mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.componentInfo?.componentChain && (
                    <p className="text-xs text-[#8a8a8a]">
                      <span className="font-medium">Component tree:</span>{' '}
                      {this.state.componentInfo.componentChain.join(' → ')}
                    </p>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <details className="mt-2">
                      <summary className="text-xs text-[#8a8a8a] cursor-pointer hover:text-[#4a4a4a]">
                        Full component stack
                      </summary>
                      <pre className="mt-2 text-xs text-[#8a8a8a] whitespace-pre-wrap overflow-auto max-h-32">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 text-sm font-medium text-[#4a4a4a] hover:text-[#1a1a1a] border border-[#ececec] rounded-[6px] hover:bg-background transition-colors"
              >
                Go Home
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-[6px] transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
